/**
 * Description: Unified capability editor page for both creating and editing capabilities
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import CapabilityForm from '@components/CapabilityForm'
import { api, Capability } from '@services/api'
import { toast } from 'react-toastify'

const CapabilityEditor: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  const [capability, setCapability] = useState<Capability | null>(null)
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isEditMode) {
      loadCapability()
    }
  }, [id])

  const loadCapability = async () => {
    if (!id) return

    try {
      setIsLoading(true)
      const data = await api.getCapabilityById(parseInt(id))
      setCapability(data)
    } catch (error) {
      toast.error('Failed to load capability')
      // eslint-disable-next-line no-console
      console.error(error)
      navigate('/capabilities')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (data: Capability) => {
    try {
      setIsSaving(true)

      if (isEditMode && id) {
        await api.updateCapability(parseInt(id), data)
        toast.success(`Capability "${data.capability_name}" updated successfully!`)
        navigate(`/capabilities/${id}`)
      } else {
        const result = await api.createCapability(data)
        toast.success(`Capability "${data.capability_name}" created successfully!`)
        navigate(`/capabilities/${result.id}`)
      }
    } catch (error: any) {
      let errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} capability`

      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const validationErrors = error.response.data.details.map((err: any) => `${err.message}`).join('; ')
        errorMessage = validationErrors
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }

      toast.error(errorMessage)
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-12 w-12 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading capability...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(isEditMode && id ? `/capabilities/${id}` : '/capabilities')}
          className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {isEditMode ? 'Edit Capability' : 'Create New Capability'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isEditMode ? capability?.capability_name : 'Add a new capability to the catalog'}
          </p>
        </div>
      </div>

      <div className="card">
        <CapabilityForm
          capability={capability || undefined}
          onSubmit={handleSubmit}
          onCancel={() => navigate(isEditMode && id ? `/capabilities/${id}` : '/capabilities')}
          isLoading={isSaving}
        />
      </div>
    </div>
  )
}

export default CapabilityEditor
